/**
 * AssessmentTakePage — timed test runner for agents. Server-shuffled question
 * + option order, countdown timer, auto-submit on timeout, result screen.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Timer, Award, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';

export default function AssessmentTakePage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { post } = useApi();

  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const startedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    post(`/me/assessments/${assessmentId}/start`)
      .then(res => {
        setAttempt(res);
        if (res.resumed) toast.info('Resumed your attempt — the clock kept running');
      })
      .catch(e => {
        toast.error(e.response?.data?.detail || 'Could not start assessment');
        navigate('/my-assessments');
      });
  }, [post, assessmentId, navigate]);

  const doSubmit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    // Mandatory free-text questions block manual submit (auto-submit on
    // timeout still goes through — server stores whatever was answered).
    if (!auto) {
      const unanswered = (attempt?.questions || []).filter(
        q => q.question_type === 'free_text' && q.required
          && !(answersRef.current[q.question_id]?.text || '').trim());
      if (unanswered.length > 0) {
        toast.error(`Answer required: "${unanswered[0].question_text.slice(0, 60)}"`);
        return;
      }
    }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        answers: (attempt?.questions || []).map(q => ({
          question_id: q.question_id,
          selected_option_ids: [...(answersRef.current[q.question_id]?.selected || [])],
          text_answer: answersRef.current[q.question_id]?.text || null,
        })),
      };
      const res = await post(`/me/assessments/${assessmentId}/submit`, payload);
      setResult(res);
      toast.success(auto ? 'Time up — answers submitted automatically' : 'Assessment submitted');
    } catch (e) {
      submittedRef.current = false;
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  }, [attempt, assessmentId, post]);

  // Countdown
  useEffect(() => {
    if (!attempt || result) return undefined;
    const tick = () => {
      const ms = new Date(attempt.deadline).getTime() - Date.now();
      const secs = Math.max(0, Math.floor(ms / 1000));
      setRemaining(secs);
      if (secs <= 0) doSubmit(true);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [attempt, result, doSubmit]);

  if (result) {
    return (
      <Card className="p-10 text-center max-w-lg mx-auto" data-testid="assessment-result">
        <Award className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <p className="font-display text-4xl font-bold">{result.score_pct}%</p>
        <p className="mt-2 inline-flex items-center gap-1 font-medium">
          {result.passed
            ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Passed</>
            : <><XCircle className="w-5 h-5 text-rose-600" /> Not passed (need {result.passing_pct}%)</>}
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          {result.correct}/{result.total} correct · {result.skipped} skipped
          {' '}· {Math.round(result.time_taken_seconds / 60)}m {result.time_taken_seconds % 60}s
        </p>
        {result.pending_review_count > 0 && (
          <p className="mt-3 text-sm rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2"
             data-testid="pending-review-note">
            {result.pending_review_count} written answer{result.pending_review_count > 1 ? 's' : ''} pending
            trainer review — your score may increase once graded.
          </p>
        )}
        <Button className="mt-6" onClick={() => navigate('/my-assessments')} data-testid="back-to-assessments-btn">
          Back to My Assessments
        </Button>
      </Card>
    );
  }

  if (!attempt) return <p className="text-sm text-muted-foreground">Preparing your assessment…</p>;

  const mins = Math.floor((remaining || 0) / 60);
  const secs = (remaining || 0) % 60;
  const answered = Object.values(answers).filter(a => (a.selected?.size || 0) > 0 || (a.text || '').trim()).length;

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="assessment-take-page">
      <div className="sticky top-14 z-30 bg-background/95 backdrop-blur border rounded-lg px-4 py-3 flex items-center justify-between">
        <span className="text-sm font-medium">{answered}/{attempt.questions.length} answered</span>
        <span className={`inline-flex items-center gap-1.5 font-mono font-bold text-lg ${remaining != null && remaining < 60 ? 'text-rose-600' : ''}`}
              data-testid="assessment-timer">
          <Timer className="w-4 h-4" /> {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </span>
      </div>

      {attempt.questions.map((q, qi) => (
        <Card key={q.question_id} className="p-4" data-testid={`take-question-${q.question_id}`}>
          <p className="font-medium text-sm mb-1">{qi + 1}. {q.question_text}</p>
          {q.multi_select && (
            <p className="text-[11px] text-muted-foreground mb-2" data-testid={`multi-hint-${q.question_id}`}>
              Select all that apply — all correct options must be chosen.
            </p>
          )}
          {q.image_url && <img src={q.image_url} alt="" className="max-h-56 rounded mb-3 mt-2" />}
          {q.question_type === 'free_text' ? (
            <div className="mt-2 space-y-1">
              {q.required && (
                <p className="text-[11px] text-rose-500 font-medium" data-testid={`required-hint-${q.question_id}`}>
                  * Answer required
                </p>
              )}
              <Textarea
                placeholder="Type your answer…"
                className="min-h-[120px]"
                maxLength={q.max_chars || undefined}
                value={answers[q.question_id]?.text || ''}
                onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { ...a[q.question_id], text: e.target.value } }))}
                data-testid={`take-freetext-${q.question_id}`}
              />
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>Reviewed and scored by your trainer</span>
                {q.max_chars && (
                  <span data-testid={`char-count-${q.question_id}`}>
                    {(answers[q.question_id]?.text || '').length}/{q.max_chars}
                  </span>
                )}
              </div>
            </div>
          ) : q.options?.length > 0 ? (
            <div className="space-y-2 mt-2">
              {q.options.map(o => (
                <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={answers[q.question_id]?.selected?.has(o.id) || false}
                    onCheckedChange={() => setAnswers(a => {
                      const cur = new Set(a[q.question_id]?.selected || []);
                      if (q.multi_select) {
                        if (cur.has(o.id)) cur.delete(o.id); else cur.add(o.id);
                      } else {
                        // Single-answer question — behave like a radio group
                        if (cur.has(o.id)) { cur.clear(); } else { cur.clear(); cur.add(o.id); }
                      }
                      return { ...a, [q.question_id]: { ...a[q.question_id], selected: cur } };
                    })}
                    data-testid={`take-option-${q.question_id}-${o.id}`}
                  />
                  {o.text}
                </label>
              ))}
            </div>
          ) : (
            <Input placeholder="Your answer"
                   value={answers[q.question_id]?.text || ''}
                   onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { ...a[q.question_id], text: e.target.value } }))}
                   data-testid={`take-text-${q.question_id}`} />
          )}
        </Card>
      ))}

      <div className="flex justify-end pb-8">
        <Button size="lg" onClick={() => doSubmit(false)} disabled={submitting}
                data-testid="submit-assessment-btn">
          {submitting ? 'Submitting…' : 'Submit Assessment'}
        </Button>
      </div>
    </div>
  );
}
